import { CheckboxItem } from 'components/CopyIteration/CheckboxItem.tsx';
import { DateTime } from 'luxon';
import {
  Button,
  Card,
  Col,
  Container,
  ErrorMessage,
  Radios,
  Row,
  SummaryList,
  TextInput,
} from 'nhsuk-react-components';
import { useState } from 'react';
import { CohortMetadata } from 'schemas/metadata/cohorts.ts';
import {
  applyAgeFilter,
  applyWordsFilter,
} from 'utils/cohortUtils.ts';
import SummaryRow from 'components/SummaryTable/Campaign/SummaryRow.tsx';
import useFetchTeamDetails from 'queries/fetch/fetchTeamDetails.ts';
import useUpdateTeamCohorts from 'queries/mutate/updateTeamCohorts.ts';
import { useNavigate } from 'react-router-dom';
import HighlightedTable from 'components/HighlightedTable/HighlightedTable.tsx';

interface IAdminCohortsSectionProps {
  teamId: string;
  allCohorts: CohortMetadata[];
  teamCohorts: CohortMetadata[];
}

function AdminCohortsSection({
  teamId,
  allCohorts,
  teamCohorts,
}: IAdminCohortsSectionProps) {
  const [maxCohortAge, setMaxCohortAge] = useState<string>('1');
  const [wordsFilter, setWordsFilter] = useState<string>('');
  const maxAgedCohorts = maxCohortAge
    ? applyAgeFilter(
      allCohorts,
      Number.parseInt(maxCohortAge, 10),
    )
    : allCohorts;

  const wordsFilteredCohorts = wordsFilter.length > 0
    ? applyWordsFilter(maxAgedCohorts, wordsFilter.split(' '))
    : maxAgedCohorts;

  const { mutate: updateTeamCohorts, error: updateError } = useUpdateTeamCohorts();

  const teamDetails = useFetchTeamDetails({ teamId });

  const [selectedTeamCohorts, setSelectedTeamCohorts] = useState<string[]>(
    teamCohorts.map(({ CohortLabel }) => CohortLabel),
  );

  const [provisionalCohort, setProvisionalCohort] = useState('');
  const [provisionalCohortErrorMessage, setProvisionalCohortErrorMessage] = useState<string>('');

  const navigate = useNavigate();
  const onClickSubmit = () => {
    updateTeamCohorts(
      {
        teamId,
        cohortLabels: selectedTeamCohorts,
      },
      {
        onSuccess: () => {
          navigate(`/admin/${teamId}/team-cohorts/confirmation`);
        },
      },
    );
  };

  return (
    <>
      <Card>
        <>
          <Card.Heading className="nhsuk-heading-s">Team details</Card.Heading>
          <SummaryList>
            <SummaryRow
              itemKey="team-name"
              itemName="Team name"
              itemValue={teamDetails.data?.team.TEAM_NAME ?? teamId}
              canUpdate={false}
            />
          </SummaryList>
          <p className="nhsuk-u-font-weight-bold">Find cohorts</p>
          <p>Search and filter available cohorts</p>

          {updateError && (
            <Card>
              <ErrorMessage>{updateError.message}</ErrorMessage>
            </Card>
          )}

          <Row>
            <Col width="full">
              <div>
                <p>Hide cohorts older than 1 month?</p>
                <Radios
                  onChange={(e) => setMaxCohortAge((e.target as HTMLInputElement).value)}
                  name="maxAgeCohort"
                  inline
                >
                  <Radios.Item
                    value="1"
                    id="older_than_1_month-yes"
                    checked={maxCohortAge === '1'}
                  >
                    Yes
                  </Radios.Item>
                  <Radios.Item
                    value=""
                    id="older_than_1_month-no"
                    checked={maxCohortAge === ''}
                  >
                    No
                  </Radios.Item>
                </Radios>
              </div>
            </Col>
          </Row>
          <Row>
            <Col width="full">
              <TextInput
                label="Start typing to search for a cohort. To remove results, type a minus sign before a word, for example '-COVID'. Leave the field blank to see all available cohorts."
                test-id="text-filter"
                onChange={(e) => setWordsFilter((e.target as HTMLInputElement).value)}
                value={wordsFilter}
              />
            </Col>
          </Row>

          <HighlightedTable aria-label="cohorts">
            <HighlightedTable.HeaderRow>
              <HighlightedTable.HeaderValue style={{ width: '15%' }}>
                <div style={{ fontWeight: 'bold' }}>
                  Chosen (
                  {selectedTeamCohorts.length}
                  )
                </div>
              </HighlightedTable.HeaderValue>
              <HighlightedTable.HeaderValue style={{ width: '40%' }}>
                Name
              </HighlightedTable.HeaderValue>
              <HighlightedTable.HeaderValue style={{ width: '20%' }}>
                Data last received
              </HighlightedTable.HeaderValue>
              <HighlightedTable.HeaderValue style={{ width: '20%' }}>
                <div style={{ textAlign: 'right' }}>People in cohort</div>
              </HighlightedTable.HeaderValue>
            </HighlightedTable.HeaderRow>
            {wordsFilteredCohorts.map((cohort) => (
              <HighlightedTable.Row key={cohort.CohortLabel}>
                <HighlightedTable.Value>
                  <CheckboxItem
                    isLabelVisible={false}
                    label={cohort.CohortLabel}
                    id={cohort.CohortLabel}
                    checked={Boolean(
                      selectedTeamCohorts?.includes(cohort.CohortLabel),
                    )}
                    onChange={({ currentTarget }) => {
                      if (
                        !selectedTeamCohorts.includes(
                          currentTarget.ariaLabel as string,
                        )
                      ) {
                        setSelectedTeamCohorts([
                          ...selectedTeamCohorts,
                          currentTarget.ariaLabel as string,
                        ]);
                        return;
                      }

                      setSelectedTeamCohorts(
                        selectedTeamCohorts.filter(
                          (selectedCohort) => selectedCohort !== currentTarget.ariaLabel,
                        ),
                      );
                    }}
                  />
                </HighlightedTable.Value>
                <HighlightedTable.Value>
                  <span style={{ fontWeight: 'bold' }}>
                    {cohort.CohortLabel}
                  </span>
                </HighlightedTable.Value>
                <HighlightedTable.Value>
                  <span className="nhsuk-u-secondary-text-colour">
                    {DateTime.fromFormat(
                      String(cohort.LastRefreshed).slice(0, 12),
                      'yyyyMMddHHmm',
                    ).toFormat('dd LLLL yyyy')}
                  </span>
                </HighlightedTable.Value>
                <HighlightedTable.Value>
                  <div
                    className="nhsuk-u-secondary-text-colour"
                    style={{ textAlign: 'right' }}
                  >
                    {cohort.MemberCount}
                  </div>
                </HighlightedTable.Value>
              </HighlightedTable.Row>
            ))}
          </HighlightedTable>

          <Container className="nhsuk-u-margin-bottom-1">
            <p className="nhsuk-u-font-weight-bold">Add cohort:</p>
            <Row>
              <Col width="three-quarters">
                <TextInput
                  label="Can't find what you're looking for?"
                  placeholder="Add cohort here"
                  value={provisionalCohort}
                  onChange={(e) => setProvisionalCohort((e.target as HTMLInputElement).value)}
                  aria-label="Enter cohort"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      setProvisionalCohortErrorMessage('');
                      if (provisionalCohort.trim().length === 0) {
                        return;
                      }

                      if (selectedTeamCohorts.includes(provisionalCohort)) {
                        setProvisionalCohortErrorMessage('This cohort is already in your team');
                        return;
                      }

                      e.preventDefault();
                      setSelectedTeamCohorts([
                        ...selectedTeamCohorts,
                        provisionalCohort,
                      ]);
                      setProvisionalCohort('');
                    }
                  }}
                />
              </Col>
              <Col width="one-quarter">
                <Button
                  small
                  test-id="add-button"
                  className="smaller-button nhsuk-u-margin-right-2 nhsuk-u-margin-top-6"
                  onClick={() => {
                    setProvisionalCohortErrorMessage('');
                    if (provisionalCohort.trim().length === 0) {
                      return;
                    }

                    if (selectedTeamCohorts.includes(provisionalCohort)) {
                      setProvisionalCohortErrorMessage(
                        'This cohort is already in your team',
                      );
                      return;
                    }

                    setSelectedTeamCohorts([
                      ...selectedTeamCohorts,
                      provisionalCohort,
                    ]);
                    setProvisionalCohort('');
                  }}
                  aria-label="Add cohort"
                >
                  Add
                </Button>
              </Col>
            </Row>
            {provisionalCohortErrorMessage && (
              <Row>
                <Col width="full">
                  <ErrorMessage>{provisionalCohortErrorMessage}</ErrorMessage>
                </Col>
              </Row>
            )}
          </Container>

          <p className="nhsuk-u-font-weight-bold">Chosen cohorts</p>
          <HighlightedTable aria-label="cohorts">
            <HighlightedTable.HeaderRow>
              <HighlightedTable.HeaderValue style={{ width: '40%' }}>
                Name
              </HighlightedTable.HeaderValue>
              <HighlightedTable.HeaderValue style={{ width: '20%' }}>
                Data last received
              </HighlightedTable.HeaderValue>
              <HighlightedTable.HeaderValue style={{ width: '20%' }}>
                <div style={{ textAlign: 'right' }}>People in cohort</div>
              </HighlightedTable.HeaderValue>
              <HighlightedTable.HeaderValue style={{ width: '20%' }}>
                <div style={{ textAlign: 'right', marginRight: '16px' }}>
                  Action
                </div>
              </HighlightedTable.HeaderValue>
            </HighlightedTable.HeaderRow>
            {selectedTeamCohorts.map((selectedTeamCohort) => {
              const foundCohort = allCohorts.find(
                ({ CohortLabel }) => CohortLabel === selectedTeamCohort,
              );
              return (
                <HighlightedTable.Row key={selectedTeamCohort}>
                  <HighlightedTable.Value>
                    <div style={{ fontWeight: 'bold' }}>
                      {selectedTeamCohort}
                      {!foundCohort && (
                        <span className="nhsuk-u-secondary-text-colour nhsuk-u-font-size-14 nhsuk-u-font-weight-normal">
                          &nbsp;(Provisional)
                        </span>
                      )}
                    </div>
                  </HighlightedTable.Value>
                  <HighlightedTable.Value>
                    <span className="nhsuk-u-secondary-text-colour">
                      {foundCohort?.LastRefreshed
                        ? DateTime.fromFormat(
                          String(foundCohort.LastRefreshed).slice(0, 12),
                          'yyyyMMddHHmm',
                        ).toFormat('dd LLLL yyyy')
                        : 'N/A'}
                    </span>
                  </HighlightedTable.Value>
                  <HighlightedTable.Value>
                    <div
                      className="nhsuk-u-secondary-text-colour"
                      style={{ textAlign: 'right' }}
                    >
                      {foundCohort?.MemberCount
                        ? foundCohort.MemberCount
                        : 'N/A'}
                    </div>
                  </HighlightedTable.Value>
                  <HighlightedTable.Value>
                    <div style={{ textAlign: 'right' }}>
                      <button
                        type="button"
                        aria-label={`remove ${selectedTeamCohort}`}
                        className="nhsuk-back-link__link nhsuk-u-margin-bottom-2"
                        style={{ color: '#d5281b' }}
                        id={`remove-cohort-${selectedTeamCohort}`}
                        onClick={(e) => {
                          setSelectedTeamCohorts(
                            selectedTeamCohorts.filter(
                              (c) => c !== selectedTeamCohort,
                            ),
                          );
                          (e.target as HTMLButtonElement).blur();
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  </HighlightedTable.Value>
                </HighlightedTable.Row>
              );
            })}
          </HighlightedTable>
        </>
      </Card>
      <Button type="button" onClick={() => onClickSubmit()}>
        Confirm team cohorts
      </Button>
    </>
  );
}
export default AdminCohortsSection;
